/**
 * Accessibility helpers for ng-bootstrap popovers that are used as small
 * confirmation dialogs (e.g. the "Copy base to all weeks" / "Reset to base"
 * confirmations in the MU Distribution grid headers).
 *
 * ng-bootstrap popovers do NOT move focus into the popover when it opens, so a
 * keyboard user who activates the trigger is left behind on the trigger (and
 * Tab then walks into the background controls) — the Yes/No buttons are never
 * reachable. These helpers implement the WAI-ARIA dialog focus pattern:
 * move focus into the dialog on open, trap Tab within it, and return focus to
 * the trigger on close (handled by the caller via the trigger element).
 *
 * The popover template is expected to wrap its content in a
 * `<div role="dialog" ...>` and the popover window is matched by
 * `popoverSelector` (e.g. `"ngb-popover-window.reset-popup"`).
 */

/**
 * Move focus to the first interactive control inside the open popover dialog.
 * Call inside a `setTimeout` from the popover `(shown)` handler so the popover
 * DOM (appended to `body`) exists before we query it.
 */
export function focusPopoverDialog(doc: Document, popoverSelector: string): void {
  // ng-bootstrap gives the popover window `role="tooltip"`, whose text content
  // (the confirmation prompt) is exposed as its accessible name. Combined with
  // the nested `role="dialog"` labelled by that same prompt, screen readers
  // announce the message twice. Strip the redundant tooltip semantics so only
  // the inner dialog announces the prompt once. Only neutralize when
  // ng-bootstrap actually applied `role="tooltip"`, so future markup changes
  // are not silently clobbered.
  const popoverWindow = doc.querySelector<HTMLElement>(popoverSelector);
  if (popoverWindow?.getAttribute("role") === "tooltip") {
    popoverWindow.setAttribute("role", "presentation");
  }
  const firstBtn = doc.querySelector<HTMLElement>(`${popoverSelector} [role="dialog"] button`);
  if (firstBtn) {
    firstBtn.focus();
    return;
  }
  doc.querySelector<HTMLElement>(`${popoverSelector} [role="dialog"]`)?.focus();
}

/**
 * Trap Tab / Shift+Tab within the open popover dialog so focus cannot escape to
 * background controls. Call from the dialog's `(keydown.tab)` handler.
 */
export function trapPopoverDialogTab(doc: Document, popoverSelector: string, event: KeyboardEvent): void {
  const buttons = Array.from(
    doc.querySelectorAll<HTMLElement>(`${popoverSelector} [role="dialog"] button`)
  );
  if (buttons.length < 2) {
    return;
  }
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  const active = doc.activeElement;
  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  }
}
